'use client'

import { useComponentTranslation } from '@core/i18n'
import {
  Box,
  Button,
  Card,
  CardBody,
  DrawerRef,
  Flex,
  FocusLock,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftAddon,
  Text,
} from '@core/ui-elements'
import { FC, ReactNode, useState } from 'react'
import { MdOutlineQrCodeScanner, MdQrCode } from 'react-icons/md'

import { FullBottomDrawer } from '../full-bottom-drawer'
import { HiddenBarcodeInput } from '../hidden-barcode-input'
import { LoadingScreen } from '../loading-screen'
import { StatCard } from '../stats-cards'

export type SetShopperProps = {
  drawerRef: DrawerRef
  title: string | ReactNode
  subtitle: string | ReactNode
  isLoading?: boolean
  handleSubmit: (id: string) => void
}

export const SetShopper: FC<SetShopperProps> = ({
  handleSubmit,
  isLoading,
  drawerRef,
  title,
  subtitle,
}) => {
  const t = useComponentTranslation('SetShopper')
  const [setID, setSetID] = useState<string>('')
  const [setScannerDisable, setSetScannerDisable] = useState<boolean>(false)

  return (
    <FullBottomDrawer
      ref={drawerRef}
      footer={
        <Button
          isDisabled={!setID}
          isLoading={isLoading}
          type="submit"
          width={'100%'}
          onClick={() => handleSubmit(setID)}
        >
          {t('Confirm')}
        </Button>
      }
      header={
        <StatCard content={<Text>{title}</Text>} label={<>{subtitle}</>} />
      }
    >
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <Flex flexDirection={'column'} gap={'1rem'} height={'100%'}>
          <Card
            backgroundColor={'blue.100'}
            borderRadius={'0.5rem'}
            colorScheme="blue"
            direction={'row'}
            overflow="hidden"
            variant={'filled'}
            width={'100%'}
          >
            <Flex
              alignItems={'center'}
              backgroundColor={'blue.200'}
              border={'0.125rem solid transparent'}
              borderRightColor={'blue.500'}
              justifyContent={'center'}
              padding={'1rem'}
            >
              <Icon as={MdOutlineQrCodeScanner} fontSize={'3rem'} />
            </Flex>

            <CardBody padding={'1.5rem 1rem'}>
              <Heading fontSize={'0.875rem'}>{t('Scan the QR Code')}</Heading>
              <Text fontSize={'0.875rem'}>{t('It has your unique ID')}</Text>
            </CardBody>
          </Card>

          <Box>
            <HiddenBarcodeInput
              disabled={setScannerDisable}
              handleSubmit={handleSubmit}
            />

            <Heading fontSize={'0.875rem'}>
              {t('Cant scan? Type your ID')}
            </Heading>

            <FocusLock isDisabled={!setScannerDisable}>
              <InputGroup marginTop={'0.5rem'} padding={'0.0625rem'}>
                <InputLeftAddon>
                  <Icon as={MdQrCode} fontSize={'1rem'} />
                </InputLeftAddon>

                <Input
                  borderLeftRadius={'0'}
                  inputMode="numeric"
                  placeholder="ID"
                  value={setID}
                  onBlur={() => setSetScannerDisable(false)}
                  onChange={(event) => setSetID(event.target.value)}
                  onClickCapture={() => setSetScannerDisable(true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSubmit(setID)
                    }
                  }}
                />
              </InputGroup>
            </FocusLock>
          </Box>
        </Flex>
      )}
    </FullBottomDrawer>
  )
}
